
import Vue from "vue";
import ShowSpinners from "@/common/components/ShowSpinners.vue";
import { required } from "vuelidate/lib/validators";
import { FormMcaOnlineReportTypeEnum } from "@/features/model/enums/FormMcaOnlineReportTypeEnum";

export default Vue.extend({
  name: "CreateMcaOnlineReport",
  components: { ShowSpinners },

  computed: {
    dialog: {
      get() {
        return this.$store.state.openOnlineReportFormModal.open;
      },
      set(val) {
        this.$store.dispatch("openOnlineReportFormModal", {
          ...this.$store.state.openOnlineReportFormModal,
          open: val,
        });
      },
    },

    isSubmitDisabled() {
      const monthlyBad = this.isAutoSendMonthlyReport && (!this.selectedIntervalDay || this.selectedEmails.length === 0);
      const dailyBad = this.isAutoSendDailyReport && (!this.mcaDailyScheduleTime || this.selectedEmails.length === 0);
      return monthlyBad || dailyBad;
    },

    companyId() {
      return this.$store.state.company.id;
    },
  },

  data() {
    const days = Array.from({ length: 28 }, (_, i) => {
      const day = i + 1;
      const suffix =
        day % 10 === 1 && day !== 11
          ? "st"
          : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
      return { key: day, value: `${day}${suffix}` };
    });

    const generateScheduleTimeList = () => {
      const list = [];
      for (let h = 0; h < 24; h++) {
        for (const m of [0, 30]) {
          const hh = h.toString().padStart(2, "0");
          const mm = m.toString().padStart(2, "0");
          list.push({ id: `${hh}:${mm}:00`, name: `${hh}:${mm}` });
        }
      }
      return list;
    };

    return {
      formMcaOnlineReportTypeEnum: FormMcaOnlineReportTypeEnum,

      isAutoSendMonthlyReport: false,
      isAutoSendDailyReport: false,

      emailList: [],
      selectedEmails: [],
      days,
      selectedIntervalDay: null,
      mcaDailyScheduleTime: null,
      scheduleTimeList: generateScheduleTimeList(),

      searchText: "",
      isLoading: false,
      loadingMessage: { title: "", detail: "" },
      isNoSearchData: false,

      page: 1,
      pageSize: 50,
      hasMore: true,
      isFetching: false,
      observer: null,
    };
  },

  methods: {
    searchEmail() {
      if (this.debounce) clearTimeout(this.debounce);
      this.debounce = setTimeout(async () => {
        this.resetPaging();
        await this.fetchEmailList();
        this.mergeSelectedIntoEmailList();
      }, 300);
    },

    resetPaging() {
      this.destroyObserver();
      this.page = 1;
      this.emailList = [];
      this.hasMore = true;
      this.isFetching = false;
    },

    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },

    handleOnClearSearchText() {
      this.searchText = "";
      this.resetPaging();

      this.fetchEmailList().then(() => {
        this.mergeSelectedIntoEmailList();
      });
    },

    handleMenuOpen() {
      let attempt = 0;
      const init = () => {
        this.$nextTick(() => {
          const list = document.querySelector(".lazy-select-menu .v-list");

          if (!list && attempt < 4) {
            attempt++;
            return setTimeout(init, 40);
          }

          this.injectFooter();
          this.initObserver();
        });
      };
      init();
    },

    initObserver() {
      const footer = document.querySelector(".lazy-select-footer");
      if (!footer) return;

      this.destroyObserver();

      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.isFetching && this.hasMore) {
          this.fetchEmailList();
        }
      });

      this.observer.observe(footer);
    },

    injectFooter() {
      const menu = document.querySelector(".lazy-select-menu .v-list");
      if (!menu) return;

      menu.querySelectorAll(".lazy-select-footer").forEach((e) => e.remove());

      const footer = document.createElement("div");
      footer.className = "lazy-select-footer grey--text text-center";
      footer.textContent = this.hasMore ? "Loading more…" : "";

      menu.appendChild(footer);
    },

    getItemText(item) {
      return `${item.email} (${item.role})`;
    },

    mergeSelectedIntoEmailList() {
      if (!Array.isArray(this.selectedEmails)) return;

      const set = new Map();

      if (this.searchText) {
        [...this.emailList, ...this.selectedEmails].forEach((item) => {
          if (item && item.id) {
            set.set(item.id, item);
          }
        });
      } else {
        [...this.selectedEmails, ...this.emailList].forEach((item) => {
          if (item && item.id) {
            set.set(item.id, item);
          }
        });
      }

      this.emailList = [...set.values()];
    },

    async fetchEmailList() {
      if (this.isFetching || !this.hasMore) return;

      this.isFetching = true;

      try {
        const res = await this.$dep.modelUseCase.getEmailsByCompanyIdResult(
          this.companyId,
          "MCA",
          this.page,
          this.pageSize,
          this.searchText
        );

        const newItems = res.items || [];
        this.isNoSearchData = newItems.length === 0;
        this.emailList = this.page === 1 ? [...newItems] : [...this.emailList, ...newItems];

        this.hasMore = newItems.length === this.pageSize;
        if (this.hasMore) this.page++;

        if (!this.searchText) {
          this.mergeSelectedIntoEmailList();
        }

        this.$nextTick(() => {
          this.injectFooter();
          this.initObserver();
        });
      } catch (err) {
        console.log(err);
      } finally {
        this.isFetching = false;
      }
    },

    async fetchNotificationMembers() {
      const res = await this.$dep.modelUseCase.getReportNotificationMembers(this.companyId, "MCA", "monthly");
      if (res) this.selectedEmails = [...res.data];
    },

    async fetchOnlineReportConfig() {
      const res = await this.$dep.modelUseCase.getOnlineReportConfig(this.companyId, "MCA", "monthly");
      if (res) {
        this.isAutoSendMonthlyReport = res.is_auto_send;
        this.selectedIntervalDay = res.interval_day;
      }
    },

    async fetchDailyConfig() {
      const res = await this.$dep.modelUseCase.getOnlineReportConfig(this.companyId, "MCA", "daily");
      if (res) {
        this.isAutoSendDailyReport = res.is_auto_send;
        this.mcaDailyScheduleTime = res.interval_time;
      }
    },

    async handleOnSubmitOnlineReport() {
      this.handleOnClearSearchText();
      this.isLoading = true;

      const monthlyBody = {
        memberIds: this.selectedEmails.map((x) => x.id),
        isAutoSend: this.isAutoSendMonthlyReport,
        intervalDay: this.selectedIntervalDay,
      };

      const dailyBody = {
        isAutoSend: this.isAutoSendDailyReport,
        intervalTime: this.mcaDailyScheduleTime,
      };

      try {
        const companyId = this.companyId;
        const monthlyResponse = await (this as any).$dep.modelUseCase.updateOnlineReport(
          companyId,
          monthlyBody,
          "MCA",
          "monthly"
        );

        if (monthlyResponse && monthlyResponse.message !== "success") {
          throw new Error(monthlyResponse.message);
        }

        const dailyResponse = await (this as any).$dep.modelUseCase.updateOnlineReport(companyId, dailyBody, "MCA", "daily");

        if (dailyResponse && dailyResponse.message !== "success") {
          throw new Error(dailyResponse.message);
        }

        this.$swal({
          icon: "success",
          title: "Success",
          text: "Report saved successfully.",
          showConfirmButton: false,
          timer: 3000,
        }).then(() => {
          this.dialog = false;
        });
      } catch (err) {
        this.$swal({
          icon: "error",
          title: "Error",
          text: "An error occurred while saving the report. Please try again.",
          showConfirmButton: false,
          timer: 1500,
        });
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    resetFormState() {
      this.destroyObserver();
      this.emailList = [];
      // this.selectedEmails = [];
      this.searchText = "";
      this.page = 1;
      this.hasMore = true;
      this.isFetching = false;
    },
  },

  watch: {
    async dialog(open) {
      if (!open) {
        this.resetFormState();
        return;
      }

      this.resetPaging();
      this.isLoading = true;

      try {
        await this.fetchNotificationMembers();
        await this.fetchEmailList();
        this.mergeSelectedIntoEmailList();

        await this.fetchOnlineReportConfig();
        await this.fetchDailyConfig();
      } catch (err) {
        console.error(err);
        this.$swal({
          icon: "error",
          title: "Error",
          text: "Failed to load report settings. Please try again.",
          showConfirmButton: false,
          timer: 1500,
        });
        this.dialog = false;
      } finally {
        this.isLoading = false;
      }
    },
  },

  validations() {
    return {
      selectedIntervalDay: { required },
      mcaDailyScheduleTime: { required },
      selectedEmails: { required },
    };
  },
});
